import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { calculateLevelBasedPoints, POINTS } from '../utils/levelCalculation';
import { calculateStreak } from '../utils/habitStreak';
import {
  habitStreakMultiplier,
  habitTarget,
  isLabeledHabit,
  habitSlots,
  completedSlotsForDate,
  habitCountForDate,
  isHabitCompleteForDate,
  intervalWeight,
} from '../utils/habitProgress';
import type { Habit, HabitCompletion, HabitSlot } from '../types';
import { resolveSlot, slotOfCompletion, withSlotIds } from '../utils/habitSlots';

/** Fraction of the day's habit XP awarded as a one-time "all intervals done" bonus. */
const FULL_SET_BONUS_FRACTION = 0.5;
import { useActivityLogStore } from './activityLogStore';
import { autoCheckInPacts } from '../utils/pactAutoCheckIn';
import { attributeDisciplineXP } from '../utils/disciplineAttribution';
import { useRecoveryStore } from './recoveryStore';
import { useGroomingStore } from './groomingStore';
import { useHabitDisciplinePrompt } from './habitDisciplinePromptStore';
import { formatDateToLocal } from '../utils/date';
import { useLocationStore } from './locationStore';
import { isSystemHabitVisible } from '../utils/systemHabitVisibility';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import { accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount } from '../lib/accountStorage';
// ⚠️ A cycle (carePracticeStore imports this store too) — safe because both only
// call each other's getState() at action time, never while the module loads.
import { useCarePracticeStore } from './carePracticeStore';
import { scheduleMissingDays } from '../utils/habitSchedule';

// Drive a habit's linked discipline ACTION on completion (separate from the
// passive XP mirror). Recovery: mark the named protocol done. Grooming: tick the
// named care item. (Hydration is handled differently — see the toggleHabit
// interception + syncHydrationHabits — because its completion is driven by the
// water tracker, not a single tap.)
function dispatchDisciplineAction(habit: Habit) {
  const action = habit.disciplineAction;
  // ⭐ `markDone` sets the one id on the SERVER's list for today (docs/DAY_LOG_STALE_WRITES.md)
  // — idempotent and never a flip, so it needs no loaded day and can't drop other entries.
  if (action?.type === 'recovery' && action.protocolId) {
    void useRecoveryStore.getState().markDone(action.protocolId);
  }
  if (action?.type === 'grooming' && action.itemId) {
    void useGroomingStore.getState().markDone(action.itemId);
  }
}

// ─── Supabase sync helpers (sync epic — same pattern as taskStore) ──────────
// The `habits` table keeps typed columns (RLS read filter + the schedule WITH
// CHECK on update + ordering) PLUS a `data` jsonb column holding the full Habit
// (carries icon/questId/goalId, which have no column). A change shows here at
// once; the cloud write follows, as a CHANGE applied to the cloud's row (see
// "Changes, not copies" below), never this tab's copy.
//
// ⚠️⚠️ A DELETED HABIT MUST STAY DELETED (found live 2026-10-18): writes used to be
// upserts, so a tab or device whose cache still held a habit deleted elsewhere
// re-INSERTED it on its next write (201 Created) — three times in one day on the
// test account, once by EatingHabitSync with no user action. And the load kept
// every cached habit the cloud no longer had, so ghosts survived reloads. Now:
// - `unsyncedIds` names the habits created HERE whose insert the cloud hasn't
//   confirmed. Only those are ever INSERTED; every other write is an UPDATE.
// - An UPDATE that finds no row means the habit was deleted elsewhere: it is
//   dropped here, never re-created (`dropDeletedElsewhere`).
// - The load keeps a cached habit the cloud didn't return only when it is still
//   unsynced; the rest are ghosts and go.
// ⚠️ Relies on "Users can read own habits" letting a user SELECT every own row:
// an UPDATE returns only rows the SELECT policies show, so a row hidden from
// SELECT would read as deleted.

const HABITS_TABLE = 'habits';

/** The row's columns, without its id (an UPDATE never sets the id). */
function habitFields(habit: Habit, userId: string) {
  return {
    user_id: userId,
    name: habit.name,
    category: habit.category ?? '',
    completed_dates: habit.completedDates ?? [],
    streak: habit.streak ?? 0,
    created_at: habit.createdAt,
    archived_at: habit.archivedAt ?? null,
    completions: habit.completions ?? [],
    // schedule MUST be a valid object w/ a valid interval or the UPDATE RLS
    // WITH CHECK rejects the write.
    schedule: habit.schedule ?? { interval: 'daily' },
    order: habit.order ?? 0,
    data: habit,
  };
}

function habitToRow(habit: Habit, userId: string) {
  return { id: habit.id, ...habitFields(habit, userId) };
}

/**
 * ⭐ Phase 3b-1: give every labelled habit permanent slot ids (`utils/habitSlots.ts`).
 * Idempotent; only the habits that actually change are written, once — and the ids are
 * given to the CLOUD's habit (`slotIdsChange`), not written from this tab's copy. Runs after
 * every load (cloud or cached boot), so existing accounts move over on their next visit.
 */
function giveSlotIds(): void {
  useHabitStore.getState().habits.forEach((h) => {
    if (withSlotIds(h) === h) return;
    // An automatic write never creates a habit: one the cloud hasn't confirmed gets its ids
    // here only (its own insert carries them).
    if (isUnsynced(h.id)) setLocal(h.id, withSlotIds(h));
    else void changeHabit(h.id, slotIdsChange);
  });
}

function rowToHabit(row: any): Habit {
  const data = row?.data;
  if (data && typeof data === 'object' && Object.keys(data).length > 0) {
    return data as Habit;
  }
  return {
    id: row.id,
    name: row.name,
    category: row.category ?? '',
    streak: row.streak ?? 0,
    completedDates: row.completed_dates ?? [],
    completions: row.completions ?? [],
    createdAt: row.created_at,
    archivedAt: row.archived_at ?? undefined,
    schedule: row.schedule ?? { interval: 'daily' },
    order: row.order ?? 0,
  } as Habit;
}

/** Created here and not yet confirmed by the cloud? (Read at SEND time.) */
const isUnsynced = (id: string): boolean => useHabitStore.getState().unsyncedIds.includes(id);

/**
 * What changed locally while a cloud load was in flight — so its answer (read before those
 * changes landed) can't undo them: a habit whose insert was confirmed meanwhile is kept, a
 * habit deleted meanwhile isn't brought back, and a habit written (or read by a write)
 * meanwhile keeps that newer copy. Null when no load is running.
 */
let loadWindow: { inserted: Set<string>; deleted: Set<string>; fresh: Map<string, Habit> } | null = null;

function markSynced(id: string): void {
  loadWindow?.inserted.add(id);
  useHabitStore.setState((s) =>
    s.unsyncedIds.includes(id) ? { unsyncedIds: s.unsyncedIds.filter((x) => x !== id) } : s,
  );
}

/** The cloud has no row for this habit any more: it was deleted on another device or tab. */
function dropDeletedElsewhere(id: string): void {
  const gone = useHabitStore.getState().habits.find((h) => h.id === id);
  loadWindow?.deleted.add(id);
  pendingChanges.delete(id);
  useHabitStore.setState((s) => ({
    habits: s.habits.filter((h) => h.id !== id),
    unsyncedIds: s.unsyncedIds.filter((x) => x !== id),
  }));
  console.warn(`[habits] "${gone?.name ?? id}" no longer exists in the cloud (deleted on another device or tab) — removed here, not re-created.`);
}

// ─── Changes, not copies ─────────────────────────────────────────────────────
// ⚠️⚠️ A STALE TAB MUST NEVER ERASE ANOTHER DEVICE'S CHANGE (2026-10-19). Design:
// docs/HABIT_STALE_WRITES.md. Every writer used to send the WHOLE habit from this tab's
// copy, which is read once per page: a tab open since before device A ticked a habit
// wrote its old copy back on its next reorder / rename / tick, and A's completion was
// gone; a tap on a stale screen minted the day's XP again. Now:
// - Every action is a CHANGE — a pure function of a habit ("done on the 19th", "fill
//   the Fajr interval", "order 3"). It shows here at once, applied to this tab's copy.
// - The write reads the habit's row, applies this tab's unsaved changes to THAT, and
//   writes it back only if the row hasn't moved since: compare-and-swap on
//   `sync_version`, which the `update_habits_sync_version` trigger bumps on every
//   UPDATE (no migration). Moved → read again; gone → deleted elsewhere.
// - The screen is then the server's habit with any later unsaved changes on top.
// - XP (activity log, Fitness attribution, pact check-in, linked action) comes only
//   from the write that made the change on the server — a stale tap that turns out
//   to be a no-op there mints nothing.
// - A habit created here whose insert isn't confirmed has no other copy: its changes
//   are decided here (and its XP minted at the tap), and its insert carries them.

/**
 * One change to one habit, applied to whichever copy is the base: the SERVER's habit
 * inside a write, this tab's copy for the screen. Pure — it can run several times.
 * Null = nothing to change. `after` runs once the change is SAVED (computed from the
 * copy it was saved onto): the XP mint, the pact check-in, the linked action.
 */
type HabitChange = (h: Habit) => { habit: Habit; after?: () => void } | null;

interface PendingChange {
  change: HabitChange;
  /** Shown before the cloud answers (the water tracker's checks aren't: they show what the cloud decides). */
  shown: boolean;
  /** Made while the habit was unsynced — this tab's copy was the only one, so its `after` already ran. */
  afterDone: boolean;
  /** `after` as computed on this tab's copy — run when there is no account to save to. */
  localAfter?: () => void;
  /** Sends that failed so far. */
  failures: number;
}

/** This tab's changes not yet saved, per habit, in the order they were made. */
const pendingChanges = new Map<string, PendingChange[]>();

const HABIT_CAS_TRIES = 5;
/** A change is sent at most this many times, then given up: one the database keeps refusing must not block the habit's later changes for good. */
const HABIT_SEND_TRIES = 3;

/** Same habit, field for field? (A change that changes nothing writes nothing.) */
const sameHabit = (a: Habit, b: Habit): boolean => JSON.stringify(a) === JSON.stringify(b);

function setLocal(id: string, habit: Habit): void {
  useHabitStore.setState((s) => ({ habits: s.habits.map((h) => (h.id === id ? habit : h)) }));
}

/** `base` (the server's habit) with this tab's unsaved changes on top — what the screen shows. */
function over(id: string, base: Habit): Habit {
  return (pendingChanges.get(id) ?? []).reduce((h, p) => (p.shown ? p.change(h)?.habit ?? h : h), base);
}

/** Show the server's habit (plus the unsaved changes on top) — every field, not just the one written. */
function show(id: string, server: Habit): void {
  const next = over(id, server);
  useHabitStore.setState((s) => ({
    habits: s.habits.map((h) => (h.id === id && !sameHabit(h, next) ? next : h)),
  }));
}

function settle(id: string, batch: readonly PendingChange[]): void {
  const left = (pendingChanges.get(id) ?? []).filter((p) => !batch.includes(p));
  if (left.length) pendingChanges.set(id, left);
  else pendingChanges.delete(id);
}

/** The changes applied in order to `base`, and what to run once they're saved. */
function applyAll(base: Habit, batch: readonly PendingChange[]): { habit: Habit; after: (() => void)[] } {
  let habit = base;
  const after: (() => void)[] = [];
  for (const p of batch) {
    const r = p.change(habit);
    if (!r) continue;
    habit = r.habit;
    if (r.after && !p.afterDone) after.push(r.after);
  }
  return { habit, after };
}

/**
 * A write that didn't go through — offline, refused (an expired session answers with a
 * refusal too, so a refusal isn't final), or the row kept moving. Its changes stay on screen
 * and are sent again with the next save of this habit, or when the browser is back online.
 * After HABIT_SEND_TRIES failed sends a change is given up (logged), and the screen shows the
 * server's habit again when this attempt read it. The water tracker's checks are never kept:
 * its next drink sends a fresh one.
 */
function failed(id: string, batch: readonly PendingChange[], error: unknown, server?: Habit): false {
  console.error('[habits] could not save — the change is kept and sent again with the next save:', error);
  const givenUp = batch.filter((p) => !p.shown || ++p.failures >= HABIT_SEND_TRIES);
  settle(id, givenUp);
  const shown = givenUp.filter((p) => p.shown);
  if (shown.length) {
    console.error(`[habits] gave up on ${shown.length} change(s) after ${HABIT_SEND_TRIES} tries.`);
    if (server) show(id, server); // stop showing what was given up
  }
  return false;
}

/**
 * Save this habit's unsaved changes (see the note above). True when the cloud accepted
 * them (or already showed them); false when they were refused, kept for later, or there
 * is no account; 'nothing' when there was nothing to send.
 * ⚠️⚠️ INSERT only for a habit created here and not yet confirmed (`unsyncedIds`); every
 * other write is a compare-and-swap UPDATE, and a habit whose row is gone is dropped here —
 * it was deleted elsewhere and must not come back.
 */
async function writeHabitChanges(id: string): Promise<boolean | 'nothing'> {
  // ⚠️⚠️ Not in the store: deleted here (`deleteHabit` dropped its changes) — or the account
  // changed, and the store holds the next one's habits (or nobody's). The changes stay pending
  // then: they are the habit's account's, and its next load sends them (2026-10-20). Dropping
  // them here lost an edit made just before a sign-out — this runs after the swap.
  const here = () => useHabitStore.getState().habits.find((h) => h.id === id);
  if (!here()) return 'nothing';
  let batch: PendingChange[] = [];
  try {
    const { user } = await syncManager.safeAuthCheck();
    batch = [...(pendingChanges.get(id) ?? [])];
    const copy = here();
    if (!copy) return 'nothing';
    if (!user) {
      // No account to save to: this tab's copy stands, and its XP is minted from it (once).
      // ⚠️⚠️ The changes it shows stay PENDING (2026-10-20): the account's next load sends them,
      // applied to the server's row. Dropped here, they were lost for good — that load took the
      // cloud's copy. (The water tracker's checks are never kept, as after any failure.)
      batch.forEach((p) => { if (p.shown && !p.afterDone) { p.localAfter?.(); p.afterDone = true; } });
      settle(id, batch.filter((p) => !p.shown));
      return false;
    }

    let inserted = false;
    if (isUnsynced(id)) {
      // This tab's copy carries every change made so far.
      const { error } = await supabase.from(HABITS_TABLE).insert([habitToRow(copy, user.id)]);
      if (!error) { markSynced(id); settle(id, batch); return true; }
      if (error.code !== '23505') {
        console.error('Failed to save new habit to Supabase:', error);
        settle(id, batch); // still unsynced: the next save inserts this tab's copy again, which carries them
        return false;
      }
      // 23505: the row is already there — an earlier insert landed but its answer was lost. It is
      // a cloud row now: the changes made since are applied to it below, like any other write.
      markSynced(id);
      inserted = true;
    }
    if (batch.length === 0) return inserted ? true : 'nothing';

    let server: Habit | undefined;
    for (let i = 0; i < HABIT_CAS_TRIES; i++) {
      const read = await supabase.from(HABITS_TABLE).select('*').eq('id', id).maybeSingle();
      if (read.error) return failed(id, batch, read.error, server);
      if (!read.data) { if (here()) dropDeletedElsewhere(id); return false; }
      server = rowToHabit(read.data);
      const { habit: next, after } = applyAll(server, batch);
      if (sameHabit(next, server)) {
        // The server already shows it (another device, or this screen was stale): nothing to write, nothing minted.
        settle(id, batch);
        loadWindow?.fresh.set(id, server);
        show(id, server);
        return true;
      }
      if (!here()) return 'nothing'; // deleted here (or the account changed) while the read was out

      const version = read.data.sync_version;
      const update = supabase.from(HABITS_TABLE).update(habitFields(next, user.id)).eq('id', id);
      const { data, error } = await (version == null ? update.is('sync_version', null) : update.eq('sync_version', version)).select('id');
      if (error) return failed(id, batch, error, server);
      if (!data || data.length === 0) continue; // moved (or deleted) since the read — read again
      settle(id, batch);
      loadWindow?.fresh.set(id, next);
      show(id, next);
      // Saved: its XP. (A throwing side effect mustn't make a landed write look failed.)
      after.forEach((run) => { try { run(); } catch (e) { console.error('[habits] after a save:', e); } });
      return true;
    }
    return failed(id, batch, new Error(`"${here()?.name ?? id}" kept changing — ${HABIT_CAS_TRIES} reads in a row`), server);
  } catch (error) {
    return failed(id, batch, error);
  }
}

/**
 * ⚠️⚠️ ONE SAVE IN FLIGHT PER HABIT (found live 2026-10-09): a tick then an untick sent two
 * concurrent writes and the tick's landed last. Now a habit's saves queue: calls made while
 * one is in flight share ONE follow-up, which sends every change unsaved when IT starts.
 * Resolves true when the write that carries this call's changes was accepted (a follow-up
 * with nothing left to send answers with the save before it).
 */
const saveRunning = new Map<string, Promise<boolean>>();
const saveQueued = new Map<string, Promise<boolean>>();
function saveHabit(id: string): Promise<boolean> {
  const queued = saveQueued.get(id);
  if (queued) return queued; // the queued follow-up will send the latest changes
  const prev = saveRunning.get(id);
  const run: Promise<boolean> = (prev ?? Promise.resolve(true)).then(async (prevOk) => {
    if (saveQueued.get(id) === run) saveQueued.delete(id);
    const r = await writeHabitChanges(id);
    return r === 'nothing' ? prevOk : r;
  });
  if (prev) saveQueued.set(id, run);
  saveRunning.set(id, run);
  void run.finally(() => { if (saveRunning.get(id) === run) saveRunning.delete(id); });
  return run;
}

/** The save that carries the latest change made to each habit (what `completeLinked` waits for). */
const latestSave = new Map<string, Promise<boolean>>();

/**
 * Make a change to a habit: shown here at once, then saved (applied to the cloud's row).
 * It is sent even when this tab's copy already shows it — the cloud's may not. Resolves with
 * the answer to the save that carries it.
 */
function changeHabit(id: string, change: HabitChange, { shown = true } = {}): Promise<boolean> {
  const here = useHabitStore.getState().habits.find((h) => h.id === id);
  if (!here) return Promise.resolve(true);
  const unsynced = isUnsynced(id);
  const r = change(here);
  const entry: PendingChange = { change, shown: shown || unsynced, afterDone: unsynced, localAfter: r?.after, failures: 0 };
  pendingChanges.set(id, [...(pendingChanges.get(id) ?? []), entry]);
  if (r && entry.shown) setLocal(id, r.habit);
  if (unsynced) r?.after?.(); // no other copy exists: decided here
  const save = saveHabit(id);
  latestSave.set(id, save);
  void save.finally(() => { if (latestSave.get(id) === save) latestSave.delete(id); });
  return save;
}

async function cloudDelete(ids: string | string[]): Promise<void> {
  const list = (Array.isArray(ids) ? ids : [ids]).filter(Boolean);
  if (list.length === 0) return;
  // ⚠️ After any save of these habits already on the wire: a delete that overtook the
  // habit's own insert would be undone by it.
  await Promise.all(list.map((id) => saveRunning.get(id)));
  try {
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return;
    const { error } = await supabase.from(HABITS_TABLE).delete().in('id', list);
    if (error) console.error('Failed to delete habit(s) from Supabase:', error);
  } catch (error) {
    console.error('Error deleting habit(s) from Supabase:', error);
  }
}

// Changes kept after a failed (offline) save go out when the browser is back online.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { [...pendingChanges.keys()].forEach((id) => void saveHabit(id)); });
}

// ─── The changes ─────────────────────────────────────────────────────────────

/** A tick's points: level × streak × interval weight. */
function tickPoints(streak: number, userLevel: number, interval: Habit['schedule']['interval']): number {
  let points = calculateLevelBasedPoints(POINTS.HABIT, userLevel);
  if (streak >= 90) points = Math.round(points * 3.0);
  else if (streak >= 60) points = Math.round(points * 2.0);
  else if (streak >= 30) points = Math.round(points * 1.6);
  else if (streak >= 14) points = Math.round(points * 1.4);
  else if (streak >= 7) points = Math.round(points * 1.2);
  else if (streak >= 3) points = Math.round(points * 1.1);
  return Math.round(points * intervalWeight(interval));
}

/**
 * A toggle habit done — or not done — on a date: the opposite of what the screen showed
 * (the caller decides). Already so on the habit it's applied to → nothing (another device
 * did it, or this screen was stale), so nothing is minted twice.
 */
function setDoneChange(date: string, done: boolean, userLevel: number, effectiveDate: string, opId?: string): HabitChange {
  return (h) => {
    if (h.completedDates.includes(date) === done) return null;
    const streak = calculateStreak(h, effectiveDate);
    const points = tickPoints(streak, userLevel, h.schedule.interval);
    let completions = h.completions || [];
    let added: HabitCompletion | null = null;
    if (!done) {
      // Uncompleting: the day's completion goes too
      completions = completions.filter((c) => c.date !== date);
    } else if (!completions.some((c) => c.date === date)) {
      // Completing: the day's completion, unless one is already there
      added = { date, points, userLevel, ...(opId ? { opId } : {}) };
      completions = [...completions, added];
    }
    const habit: Habit = {
      ...h,
      completedDates: done ? [...h.completedDates, date].sort() : h.completedDates.filter((d) => d !== date),
      completions,
      streak,
    };
    return { habit, after: done ? () => toggledDone(habit, date, added) : undefined };
  };
}

/** A toggle habit turned done by a saved write: the pact check-in, the day's XP (when that write added its record), the linked action. */
function toggledDone(h: Habit, date: string, added: HabitCompletion | null): void {
  // pacted habit? publish the pact check-in (silent no-op otherwise)
  void autoCheckInPacts({ type: 'habit', id: h.id });
  if (added) {
    useActivityLogStore.getState().addActivity({
      activityType: 'habit',
      activityName: h.name,
      domainId: h.category,
      xpEarned: added.points,
      metadata: {
        habitId: h.id,
        streak: h.streak,
        interval: h.schedule.interval,
        ...(h.icon ? { icon: h.icon } : {})
      },
      completedAt: `${date}T12:00:00.000Z`
    });
    // Mirror the earned XP into the attached discipline's parameters
    // (no new mint — see disciplineAttribution).
    void attributeDisciplineXP(h.discipline, added.points, h.name);
  }
  // Drive the linked discipline ACTION (mark a recovery protocol done).
  dispatchDisciplineAction(h);
}

/**
 * One completion toward a 'times per day' habit: the named interval (`slotRef`, its id) —
 * or, with none named, the next one open on the habit it's applied to — nothing when that
 * interval is done or the count is at its target. The full-set bonus when this change
 * reaches the target. Idempotent by `opId`.
 */
function incrementChange(date: string, slotRef: string | undefined, userLevel: number, effectiveDate: string, opId?: string): HabitChange {
  return (h) => {
    // A linked operation's tick is there already (a retry, or its answer was lost): never a second.
    if (opId && (h.completions ?? []).some((c) => c.opId === opId)) return null;
    // ⭐ The intervals and target it had THAT day (they change with the meal plan, 2026-10-18).
    const target = habitTarget(h, date);
    const prevCount = habitCountForDate(h, date);
    let fillSlot: HabitSlot | undefined;
    if (isLabeledHabit(h, date)) {
      const slots = habitSlots(h, date)!;
      const done = completedSlotsForDate(h, date);
      if (slotRef != null) {
        const s = resolveSlot(slots, slotRef);
        if (!s || done.has(s.id)) return null; // unknown / ambiguous / already done
        fillSlot = s;
      } else {
        fillSlot = slots.find((s) => !done.has(s.id));
        if (fillSlot == null) return null; // every slot already done
      }
    } else if (prevCount >= target) {
      return null; // already at the daily target
    }

    const reachedTarget = prevCount + 1 >= target;
    const fullSet = reachedTarget && prevCount < target;
    const completedDates = reachedTarget && !h.completedDates.includes(date)
      ? [...h.completedDates, date].sort()
      : h.completedDates;
    const streak = calculateStreak({ ...h, completedDates }, effectiveDate);
    const points = Math.round(
      calculateLevelBasedPoints(POINTS.HABIT, userLevel) *
        habitStreakMultiplier(streak) *
        intervalWeight(h.schedule.interval)
    );
    const real: HabitCompletion = {
      date, points, userLevel, completionType: 'quick',
      ...(fillSlot ? { slot: fillSlot.label, slotId: fillSlot.id } : {}),
      ...(opId ? { opId } : {}),
    };
    let completions = [...(h.completions ?? []), real];
    // Full-set bonus: once, the moment the final interval completes.
    let bonus: HabitCompletion | null = null;
    if (fullSet && target > 1) {
      const todaysReal = completions
        .filter((c) => c.date === date && !c.bonus)
        .reduce((s, c) => s + c.points, 0);
      const bonusPoints = Math.round(todaysReal * FULL_SET_BONUS_FRACTION);
      if (bonusPoints > 0) {
        bonus = { date, points: bonusPoints, userLevel, bonus: true };
        completions = [...completions, bonus];
      }
    }
    const habit: Habit = { ...h, completedDates, completions, streak };
    return { habit, after: () => incremented(habit, date, real, fullSet, target, bonus) };
  };
}

/** A saved increment's XP events. */
function incremented(h: Habit, date: string, real: HabitCompletion, fullSet: boolean, target: number, bonus: HabitCompletion | null): void {
  // Each increment is its own XP event in the activity log…
  useActivityLogStore.getState().addActivity({
    activityType: 'habit',
    activityName: real.slot ? `${h.name} · ${real.slot}` : h.name,
    domainId: h.category,
    xpEarned: real.points,
    metadata: { habitId: h.id, streak: h.streak, interval: h.schedule.interval },
    completedAt: `${date}T12:00:00.000Z`,
  });
  // Attribute each real increment's XP to the attached discipline (the
  // full-set bonus is a consistency reward, not fitness effort — skipped).
  void attributeDisciplineXP(h.discipline, real.points, h.name);
  // The habit counts as DONE for the day once the target is reached —
  // that's the pact check-in moment for count habits.
  if (fullSet) void autoCheckInPacts({ type: 'habit', id: h.id });
  // …plus a separate event for the full-set bonus, when earned by this write.
  if (bonus) {
    useActivityLogStore.getState().addActivity({
      activityType: 'habit',
      activityName: `${h.name} · all ${target} complete`,
      domainId: h.category,
      xpEarned: bonus.points,
      metadata: { habitId: h.id, streak: h.streak, interval: h.schedule.interval, bonus: true, ...(h.icon ? { icon: h.icon } : {}) },
      completedAt: `${date}T12:00:00.000Z`,
    });
  }
}

/**
 * Undo one completion for the date: the named interval's (`slotRef`, its id), else the most
 * recent that still counts — only while the habit it's applied to still has it. Below target
 * the full-set bonus and the done date go.
 */
function decrementChange(date: string, slotRef: string | undefined, effectiveDate: string): HabitChange {
  return (h) => {
    const labeled = isLabeledHabit(h, date);
    const real = (h.completions ?? []).filter((c) => c.date === date && !c.bonus);
    if (real.length === 0) return null;
    // ⭐ Labelled: only completions of a CURRENT slot can be undone here (by id, or a
    // label only one slot has); a removed slot's records stay as history.
    const slots = labeled ? habitSlots(h, date)! : null;
    const target = slotRef != null && slots ? resolveSlot(slots, slotRef) : null;
    if (labeled && slotRef != null && !target) return null; // unknown / ambiguous
    const belongs = (c: HabitCompletion) => {
      if (!slots) return true;
      const s = slotOfCompletion(slots, c);
      return !!s && (!target || s.id === target.id);
    };
    if (labeled && !real.some(belongs)) return null; // nothing for that slot (undone elsewhere already)

    const dayTarget = habitTarget(h, date);
    const all = [...(h.completions ?? [])];
    // Drop the targeted slot's record (labelled + slot given), else the most recent
    // that still counts.
    let removeIdx = -1;
    for (let i = all.length - 1; i >= 0; i--) {
      const c = all[i];
      if (c.date !== date || c.bonus) continue;
      if (belongs(c)) { removeIdx = i; break; }
    }
    if (removeIdx < 0) return null;
    all.splice(removeIdx, 1);

    const newCount = habitCountForDate({ ...h, completions: all }, date);
    // Falling below target revokes the full-set bonus for the day.
    const completions = newCount < dayTarget
      ? all.filter(c => !(c.date === date && c.bonus))
      : all;
    const completedDates = newCount < dayTarget
      ? h.completedDates.filter(d => d !== date)
      : h.completedDates;
    const streak = calculateStreak({ ...h, completedDates }, effectiveDate);
    return { habit: { ...h, completedDates, completions, streak } };
  };
}

/** Undo exactly the completion an operation made (and the day's bonus/done state it carried). */
function uncompleteOpChange(opId: string, effectiveDate: string): HabitChange {
  return (h) => {
    const made = (h.completions ?? []).find((c) => c.opId === opId);
    if (!made) return null; // nothing that operation made is left
    const date = made.date;
    const all = (h.completions ?? []).filter((c) => c.opId !== opId);
    const below = habitCountForDate({ ...h, completions: all }, date) < habitTarget(h, date);
    const completions = below ? all.filter((c) => !(c.date === date && c.bonus)) : all;
    const completedDates = below ? h.completedDates.filter((d) => d !== date) : h.completedDates;
    const streak = calculateStreak({ ...h, completedDates }, effectiveDate);
    return { habit: { ...h, completions, completedDates, streak } };
  };
}

/**
 * A plain patch: fields only. ⚠️ Never completions / done dates / streak — from this tab's
 * habit they'd be a COPY (a type error, even through a variable: `never`).
 */
export type HabitPatch = Omit<Partial<Habit>, 'id' | 'completions' | 'completedDates' | 'streak'>
  & { id?: never; completions?: never; completedDates?: never; streak?: never };

/**
 * Set fields: a plain patch, or — for a patch computed from the habit itself (its
 * completions moved to new interval ids…) — a function of the habit it's applied to.
 */
function patchChange(updates: HabitPatch | ((h: Habit) => Partial<Habit> | null)): HabitChange {
  return (h) => {
    const p = typeof updates === 'function' ? updates(h) : updates;
    if (!p) return null;
    // ⭐ Slots (phase 3b-1): new `slots` bring their label mirror; labels alone (older
    // callers) keep the ids by position. Either way `withSlotIds` keeps the two in step.
    const patch = p.slots ? { ...p, intervalLabels: p.slots.map(s => s.label) } : p;
    return { habit: withSlotIds({ ...h, ...patch }) };
  };
}

const setOrderChange = (order: number): HabitChange => (h) => (h.order === order ? null : { habit: { ...h, order } });
// Protected prayer habits can't be archived (managed by Prayer Settings).
const archiveChange = (at: string): HabitChange => (h) => (h.archivedAt || h.system ? null : { habit: { ...h, archivedAt: at } });
const restoreChange: HabitChange = (h) => (h.archivedAt ? { habit: { ...h, archivedAt: undefined } } : null);
const slotIdsChange: HabitChange = (h) => { const n = withSlotIds(h); return n === h ? null : { habit: n }; };

// ─── Hydration-linked habits: decided on the SERVER's row ────────────────────
// ⚠️⚠️ A STALE TAB MUST NOT RE-TICK A HABIT ANOTHER DEVICE ALREADY TICKED (2026-10-19).
// The water tracker drives these habits (syncHydrationHabits, after every drink). The
// tick was decided on THIS tab's copy, which is read once per page: a tab open since
// before device A reached the goal had no completion for today, so its next drink
// minted the day again (a second activity_logs row, a second Fitness attribution, a
// second pact check-in) and wrote its whole stale copy over A's row. Now each check is
// a change applied to the habit's row (like every other change), and isn't shown before
// the cloud decides. Only the write that ADDS the day's completion mints, and only when
// the ledger has no mint for that day yet: an older build's whole-row write can erase
// the completion — putting it back restores the level XP, but the day was already minted.

/** The ledger stamp of a habit's day mint (every habit mint uses it). */
const mintStamp = (date: string) => `${date}T12:00:00.000Z`;

/**
 * The water tracker's day applied to a habit: done, with the day's completion (added
 * once), when the goal is met; not done when it isn't — the completion stays, so the
 * day's XP is minted once and never revoked. Null when the habit already shows that.
 */
function withHydrationDay(
  h: Habit, date: string, met: boolean, userLevel: number, streakDate: string,
): { habit: Habit; added: HabitCompletion | null } | null {
  const dated = h.completedDates.includes(date);
  const completions = h.completions ?? [];
  if (!met) return dated ? { habit: { ...h, completedDates: h.completedDates.filter(d => d !== date) }, added: null } : null;
  const recorded = completions.some(c => c.date === date);
  if (dated && recorded) return null;
  const streak = calculateStreak(h, streakDate);
  const added = recorded ? null : { date, points: tickPoints(streak, userLevel, h.schedule.interval), userLevel };
  return {
    habit: {
      ...h,
      completedDates: dated ? h.completedDates : [...h.completedDates, date].sort(),
      completions: added ? [...completions, added] : completions,
      streak,
    },
    added,
  };
}

/** Does the ledger already hold this habit's mint for the day? An unreadable ledger answers no. */
async function dayMinted(habitId: string, date: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('activity_logs')
    .select('id')
    .eq('activity_type', 'habit')
    .eq('metadata->>habitId', habitId)
    .eq('completed_at', mintStamp(date))
    .limit(1);
  if (error) { console.error('[habits] could not read the activity log:', error); return false; }
  return (data ?? []).length > 0;
}

/** The day's XP event for a completion just added: the activity log, the pact check-in, the discipline mirror — unless the ledger has it. */
async function mintHydrationDay(h: Habit, c: HabitCompletion): Promise<void> {
  if (await dayMinted(h.id, c.date)) return;
  void autoCheckInPacts({ type: 'habit', id: h.id });
  useActivityLogStore.getState().addActivity({
    activityType: 'habit',
    activityName: h.name,
    domainId: h.category,
    xpEarned: c.points,
    metadata: { habitId: h.id, streak: h.streak, interval: h.schedule.interval, ...(h.icon ? { icon: h.icon } : {}) },
    completedAt: mintStamp(c.date),
  });
  void attributeDisciplineXP(h.discipline, c.points, h.name);
}

/** The water tracker's day for a habit, as a change. Archived or unlinked from the tracker (elsewhere) → left alone. */
function hydrationDayChange(date: string, met: boolean): HabitChange {
  const { userLevel, testDate } = useHabitStore.getState();
  const streakDate = testDate || new Date().toISOString().split('T')[0];
  return (h) => {
    if (h.disciplineAction?.type !== 'hydration' || h.archivedAt) return null;
    const next = withHydrationDay(h, date, met, userLevel, streakDate);
    if (!next) return null;
    const { habit, added } = next;
    return { habit, after: added ? () => void mintHydrationDay(habit, added) : undefined };
  };
}

// ─── Reading every habit: the load, and the screen catching up on focus ──────

/** When every habit was last read (a load or a refresh); 0 = never. */
let lastFullRead = 0;

/**
 * Read every habit and show them. Cloud wins on id — with this tab's unsaved changes on
 * top, and a habit written (or read by a write) meanwhile keeping that newer copy; a cached
 * habit the cloud didn't return is kept only while it is unsynced (created here): the rest
 * were deleted elsewhere (see the header note). Throws when the cloud can't be read
 * (nothing changes then). Null when the answer came after a change of account (`at`, noted
 * before the caller's first await): it is the account that left's, and nothing changes.
 * Otherwise the habits it kept over the cloud's copy — still unsynced, or shown with this
 * tab's unsaved changes on top: the cloud has neither, so the load sends them.
 */
async function readAllHabits(userId: string, at: number): Promise<string[] | null> {
  lastFullRead = Date.now();
  const win = { inserted: new Set<string>(), deleted: new Set<string>(), fresh: new Map<string, Habit>() };
  loadWindow = win;
  let data: unknown[] | null;
  try {
    const res = await supabase
      .from(HABITS_TABLE)
      .select('*');
    // An answer for an account that has since signed out is not this copy's.
    if (at !== accountGeneration()) return null;
    if (res.error) throw res.error;
    data = res.data;
  } finally {
    if (loadWindow === win) loadWindow = null;
  }

  const cloudHabits = (data ?? []).map(rowToHabit);
  const cloudIds = new Set(cloudHabits.map((h) => h.id));

  let kept: string[] = [];
  useHabitStore.setState((state) => {
    const cacheIsOurs =
      state.cachedUserId === null || state.cachedUserId === userId;
    const unsynced = new Set(cacheIsOurs ? state.unsyncedIds : []);

    const byId = new Map<string, Habit>();
    if (cacheIsOurs) {
      // ⚠️⚠️ A cached habit the cloud didn't return was deleted elsewhere — a ghost
      // that the next write used to re-insert. Kept only when it was created here
      // and not yet confirmed (or confirmed while this read was in flight).
      state.habits.forEach((h) => {
        if (cloudIds.has(h.id) || unsynced.has(h.id) || win.inserted.has(h.id)) byId.set(h.id, h);
      });
    }
    // Cloud wins on conflict (synced source of truth) — except a habit deleted
    // here while the read was in flight, and one written (or read by a write)
    // meanwhile, whose copy is newer. Unsaved changes stay on top.
    cloudHabits.forEach((h) => {
      if (!win.deleted.has(h.id)) byId.set(h.id, over(h.id, win.fresh.get(h.id) ?? h));
    });

    // Still unconfirmed: kept, and not returned by the cloud.
    const unsyncedIds = [...unsynced].filter((id) => byId.has(id) && !cloudIds.has(id));
    kept = [...unsyncedIds, ...cloudHabits.filter((h) => pendingChanges.has(h.id)).map((h) => h.id)];
    return {
      habits: Array.from(byId.values()),
      unsyncedIds,
      initializedUserId: userId,
      cachedUserId: userId,
    };
  });
  return kept;
}

// ⭐ THE SCREEN CATCHES UP ON FOCUS (2026-10-19). Writes are safe without this (each change
// is applied to the cloud's row), but a tab left open SHOWED the habits it loaded — another
// device's ticks, renames and deletions — until it wrote to that habit or reloaded, and
// summed its level and streaks from that copy. So when the tab comes back (visible again,
// or focused) every habit is read again: at most once a minute, and only once the store has
// loaded for the signed-in account.
// ⚠️⚠️ READ ONLY: no slot-id migration, no hydration re-sync, no XP — a refresh that wrote
// would decide from this tab's copy again (see a-display-refresh-can-write).

const HABITS_REFRESH_MS = 60_000;
let refreshing: Promise<void> | null = null;

export const refreshHabitsOnFocus = {
  /** Re-read every habit — when due (see above). */
  run(): Promise<void> {
    if (refreshing) return refreshing;
    if (Date.now() - lastFullRead < HABITS_REFRESH_MS) return Promise.resolve();
    refreshing = (async () => {
      const at = accountGeneration();
      try {
        const { user } = await syncManager.safeAuthCheck();
        // Not loaded yet, signed out, or another account signed in since the load: not this store's to refresh.
        if (!user || user.id !== useHabitStore.getState().initializedUserId) return;
        await readAllHabits(user.id, at);
      } catch (error) {
        console.error('[habits] could not re-read the habits:', error);
      }
    })().finally(() => { refreshing = null; });
    return refreshing;
  },
  /** Forget when the habits were last read (tests). */
  forget(): void { lastFullRead = 0; },
};

if (typeof document !== 'undefined') {
  const cameBack = () => { if (document.visibilityState === 'visible') void refreshHabitsOnFocus.run(); };
  document.addEventListener('visibilitychange', cameBack);
  window.addEventListener('focus', cameBack);
}

interface HabitState {
  habits: Habit[];
  testDate: string | null;
  userLevel: number;
  initializedUserId: string | null;
  /**
   * The last load failed — Growth Foundations says "couldn't check" (and offers Retry) instead of
   * "checking". Not saved: a reload loads again.
   */
  loadFailed: boolean;
  cachedUserId: string | null;
  /**
   * Habits created on THIS device whose insert the cloud hasn't confirmed yet (offline, or
   * still on the wire). The only habits ever inserted, and the only cached habits a load
   * keeps when the cloud doesn't return them. Persisted with the cache.
   */
  unsyncedIds: string[];
  initialize: () => Promise<void>;
  /** Returns the new habit's id. */
  addHabit: (habit: Omit<Habit, 'id' | 'streak' | 'completedDates' | 'createdAt' | 'order'>) => string;
  toggleHabit: (habitId: string, date: string, opts?: LinkedTickOpts) => void;
  /**
   * Mark a habit complete for a date (idempotent; minted once a day) — used by the
   * hydration sync. ⚠️ Decided on the habit's ROW in the cloud, not this tab's copy.
   */
  markHabitCompleteForDate: (habitId: string, date: string) => Promise<void>;
  /** Clear a habit's done state for a date, keeping the day's XP record (decided on the cloud row too). */
  unmarkHabitCompleteForDate: (habitId: string, date: string) => Promise<void>;
  /**
   * Reconcile hydration-linked habits against the water tracker: done (minted once
   * a day) while today's intake meets the target, not done below it (the day's XP
   * stays). ⚠️ Each habit is decided on its row in the cloud, never on this tab's
   * copy — a stale tab must not re-tick (and re-mint) a day another device ticked.
   * Called by hydrationStore after intake/target changes.
   */
  syncHydrationHabits: (todayIntakeMl: number, todayTargetMl: number) => Promise<void>;
  /**
   * Add one completion toward a 'times per day' habit's daily target. For
   * labelled habits pass the named `slot` to fill (omit to fill the next empty
   * slot). Awards a one-time full-set bonus when the final interval completes.
   */
  incrementHabit: (habitId: string, date: string, slot?: string, opts?: LinkedTickOpts) => void;
  /**
   * ⭐ The habit half of a Nutrition-linked eat-and-tick (phase 3b-2): tick the interval
   * (`slotRef` = its id or a unique label; null = the next open one / the whole habit)
   * with the operation id, then WAIT for the save. Idempotent by `opId` — a retry
   * re-saves, never adds a second. An interval already done (e.g. ticked "habit only"
   * earlier) is left as it is: ok, nothing created. `ok` is the database's answer.
   */
  completeLinked: (habitId: string, date: string, slotRef: string | null, opId: string) => Promise<{ ok: boolean; created: boolean }>;
  /** Undo exactly the completion an operation created (and the day's bonus/done state it carried); waits for the save. */
  uncompleteByOp: (habitId: string, opId: string) => Promise<boolean>;
  /**
   * Undo one completion for the date. For labelled habits pass the `slot` to
   * clear (omit to remove the most recent). Removes the full-set bonus if the
   * day drops back below target.
   */
  decrementHabit: (habitId: string, date: string, slot?: string) => void;
  /**
   * Set fields. ⚠️ A patch computed FROM the habit (its completions moved to new interval
   * ids — `toPrayerSlots`, `syncEatingHabit`) must be a function: it is applied to the
   * cloud's habit, not this tab's copy (a plain patch can't carry completions).
   */
  updateHabit: (habitId: string, updates: HabitPatch | ((h: Habit) => Partial<Habit> | null)) => void;
  archiveHabit: (habitId: string) => void;
  restoreHabit: (habitId: string) => void;
  deleteHabit: (habitId: string, opts?: { force?: boolean }) => void;
  getActiveHabits: () => Habit[];
  getArchivedHabits: () => Habit[];
  setTestDate: (date: string | null) => void;
  reorderHabits: (sourceId: string, targetId: string) => void;
  setUserLevel: (level: number) => void;
}

/** A tick made by the Nutrition- or Supplements-linked confirmation (phase 3b-2; step 6 of the
 *  Supplements pass): skips the prompt and stamps the completion with the operation id. */
export interface LinkedTickOpts {
  opId?: string;
  confirmed?: boolean;
}

/** Is this habit linked to Nutrition (its ticks ask what you ate)? */
const isNutritionLinked = (h: Habit) => h.disciplineAction?.type === 'nutrition';
/** Is this habit the supplements habit (its ticks ask which doses were taken — step 6, 2026-10-23)? */
const isSupplementsLinked = (h: Habit) => h.disciplineAction?.type === 'supplements';

export const useHabitStore = create<HabitState>()(
  persist<HabitState, [], [], any>(
    (set, get) => ({
      habits: [],
      testDate: null,
      userLevel: 1,
      initializedUserId: null,
      loadFailed: false,
      cachedUserId: null,
      unsyncedIds: [],

      // Load-on-login, keyed by user id (re-runs on account switch): `readAllHabits`
      // (cloud wins on id, unsaved changes on top, ghosts go — see header note), then
      // the slot-id migration.
      initialize: () => dedupeInit('habits', async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;
          if (get().initializedUserId === user.id) return;
          if (get().loadFailed) set({ loadFailed: false });
          // Cache-first boot: while this user's local cache is fresh, skip the
          // cloud read entirely (write-through keeps cloud current meanwhile).
          if (get().cachedUserId === user.id && isCloudFresh('habits', user.id)) {
            set({ initializedUserId: user.id });
            giveSlotIds();
            return;
          }

          const kept = await readAllHabits(user.id, at);
          if (!kept) return; // the account that left's: not fresh, nothing to give ids, nothing to send
          markCloudFresh('habits', user.id);
          giveSlotIds();
          // ⚠️⚠️ What the load kept over the cloud (2026-10-20): a habit made here is INSERTED (with
          // its slot ids, given just above), and this tab's unsaved changes are applied to the
          // server's row — never a copy. They used to wait on this device for the habit's next change.
          kept.forEach((id) => void saveHabit(id));
        } catch (error) {
          console.error('Failed to initialize habits from Supabase:', error);
          // ⚠️ Only this account's: a load that started before a sign-out says nothing about the next.
          if (at === accountGeneration()) set({ loadFailed: true });
        }
      }),

      setUserLevel: (level) => set({ userLevel: level }),
      setTestDate: (date) => set({ testDate: date }),

      addHabit: (habitData) => {
        /* ⚠️⚠️ THE BACKSTOP (owner, 2026-10-04): a weekly / monthly / yearly habit
           with no day is never due, so it is never stored. Every path asks for the
           days first (forms, Inbox, pacts, AI, imports — utils/habitDaysPrompt.ts);
           reaching this line means a NEW path skipped that. Returns '' — no id. */
        if (scheduleMissingDays(habitData.schedule)) {
          console.error('[habits] refused: a weekly / monthly / yearly habit needs at least one day —', habitData.name);
          return '';
        }
        const habit: Habit = withSlotIds({
          ...habitData,
          ...(habitData.slots ? { intervalLabels: habitData.slots.map(s => s.label) } : {}),
          id: crypto.randomUUID(),
          streak: 0,
          completedDates: [],
          completions: [],
          createdAt: new Date().toISOString(),
          order: get().habits.length
        });

        set((state) => ({
          habits: [...state.habits, habit],
          unsyncedIds: [...state.unsyncedIds, habit.id], // ← INSERTED until the cloud confirms it
        }));

        void saveHabit(habit.id);
        return habit.id;
      },

      toggleHabit: (habitId, date, opts) => {
        const habit = get().habits.find(h => h.id === habitId);
        if (!habit) return;

        // Hydration-linked habits are driven by the water tracker, not a tap:
        // open the log prompt; completion happens via syncHydrationHabits when the
        // daily target is met. Never toggle directly (and once met it's locked).
        if (habit.disciplineAction?.type === 'hydration') {
          if (!habit.completedDates.includes(date)) {
            useHabitDisciplinePrompt.getState().requestHydration({ habitId: habit.id, habitName: habit.name });
          }
          return;
        }

        // Count habits (targetPerDay > 1) can't be a single toggle — any legacy
        // "complete" tap adds one toward the daily target instead.
        if (habitTarget(habit, date) > 1) {
          get().incrementHabit(habitId, date, undefined, opts);
          return;
        }

        // ⭐ Nutrition-linked (phase 3b-2): ticking asks what you ate instead of completing.
        // Unticking is direct — it removes the completion only; any food logged stays.
        if (isNutritionLinked(habit) && !opts?.confirmed && !habit.completedDates.includes(date)) {
          useHabitDisciplinePrompt.getState().requestNutrition({ habitId, habitName: habit.name, date, slotId: null, slotLabel: null });
          return;
        }
        // ⭐ The supplements habit (2026-10-23): the same — ticking asks which doses were taken;
        // unticking is direct (it removes the completion only; every dose's mark stays).
        if (isSupplementsLinked(habit) && !opts?.confirmed && !habit.completedDates.includes(date)) {
          useHabitDisciplinePrompt.getState().requestSupplements({ habitId, habitName: habit.name, date, slotId: null, slotLabel: null });
          return;
        }

        // ⚠️⚠️ The INTENT — the opposite of what this screen shows — is applied to the cloud's
        // habit (`setDoneChange`): a stale screen can't undo another device's tick, nor tick (and
        // mint) a day already done there. XP follows the write that adds the day's record.
        const done = !habit.completedDates.includes(date);
        const effectiveDate = get().testDate || new Date().toISOString().split('T')[0];
        void changeHabit(habitId, setDoneChange(date, done, get().userLevel, effectiveDate, opts?.opId));
      },

      // ⚠️⚠️ All three decide on the habit's ROW in the cloud (`hydrationDayChange`), never
      // on this tab's copy: a tab open since before another device ticked the day would
      // tick — and mint — it again. XP/streak are minted exactly ONCE per day, keyed on the
      // day's completion record; re-crossing the target after an undo never re-mints or
      // revokes it. Not shown before the cloud decides.
      markHabitCompleteForDate: (habitId, date) =>
        changeHabit(habitId, hydrationDayChange(date, true), { shown: false }).then(() => undefined),

      // Clear the locked/done state (e.g. hydration dropped below target) but KEEP the
      // day's completion record so the once-per-day XP isn't re-minted/revoked.
      unmarkHabitCompleteForDate: (habitId, date) =>
        changeHabit(habitId, hydrationDayChange(date, false), { shown: false }).then(() => undefined),

      syncHydrationHabits: (todayIntakeMl, todayTargetMl) => {
        // Completion MIRRORS the water tracker: locked only at ≥100%, interactive
        // below (so the user keeps adding water). Two-way; XP mints once/day. Every
        // linked habit is checked against the cloud — this tab's copy may be stale
        // either way (ticked or un-ticked elsewhere).
        const todayYmd = get().testDate || formatDateToLocal(new Date());
        const met = todayTargetMl > 0 && todayIntakeMl >= todayTargetMl;
        const linked = get().habits.filter((h) => h.disciplineAction?.type === 'hydration' && !h.archivedAt);
        return Promise.all(linked.map((h) =>
          met ? get().markHabitCompleteForDate(h.id, todayYmd) : get().unmarkHabitCompleteForDate(h.id, todayYmd),
        )).then(() => undefined);
      },

      incrementHabit: (habitId, date, slot, opts) => {
        const habit = get().habits.find(h => h.id === habitId);
        if (!habit) return;
        // ⭐ The intervals and target it had THAT day (they change with the meal plan, 2026-10-18).
        const target = habitTarget(habit, date);
        const labeled = isLabeledHabit(habit, date);

        // Resolve which named slot to fill (labelled habits only); bail on no-ops.
        // ⭐ `slot` is the slot's permanent ID — or, from older callers and stored
        // routine steps, a label only one slot has (phase 3b-1). Never guessed.
        let fillSlot: HabitSlot | undefined;
        const prevCount = habitCountForDate(habit, date);
        if (labeled) {
          const slots = habitSlots(habit, date)!;
          const done = completedSlotsForDate(habit, date);
          if (slot != null) {
            const s = resolveSlot(slots, slot);
            if (!s || done.has(s.id)) return; // unknown / ambiguous / already done
            fillSlot = s;
          } else {
            fillSlot = slots.find((s) => !done.has(s.id));
            if (fillSlot == null) return; // every slot already done
          }
        } else if (prevCount >= target) {
          return; // already at the daily target
        }

        // ⭐ Nutrition-linked (phase 3b-2): ticking an interval asks what you ate — from
        // EVERY screen, since they all come through here (owner: "everywhere").
        if (isNutritionLinked(habit) && !opts?.confirmed) {
          useHabitDisciplinePrompt.getState().requestNutrition({
            habitId, habitName: habit.name, date, slotId: fillSlot?.id ?? null, slotLabel: fillSlot?.label ?? null,
          });
          return;
        }
        // ⭐ The supplements habit (2026-10-23): ticking an interval asks which of that time of
        // day's doses were taken — from every screen, as above.
        if (isSupplementsLinked(habit) && !opts?.confirmed) {
          useHabitDisciplinePrompt.getState().requestSupplements({
            habitId, habitName: habit.name, date, slotId: fillSlot?.id ?? null, slotLabel: fillSlot?.label ?? null,
          });
          return;
        }

        // ⚠️⚠️ Applied to the cloud's habit (`incrementChange`): the interval named here, or —
        // none named — the next one open THERE, so a stale screen never refills (and re-mints)
        // an interval another device filled, nor overwrites it. XP follows the write.
        const effectiveDate = get().testDate || new Date().toISOString().split('T')[0];
        void changeHabit(habitId, incrementChange(date, slot != null ? fillSlot?.id : undefined, get().userLevel, effectiveDate, opts?.opId));
      },

      decrementHabit: (habitId, date, slot) => {
        const habit = get().habits.find(h => h.id === habitId);
        if (!habit) return;
        const labeled = isLabeledHabit(habit, date);
        const real = (habit.completions ?? []).filter(c => c.date === date && !c.bonus);
        if (real.length === 0) return;
        // ⭐ Labelled: only completions of a CURRENT slot can be undone here (by id, or a
        // label only one slot has); a removed slot's records stay as history.
        const slots = labeled ? habitSlots(habit, date)! : null;
        const target = slot != null && slots ? resolveSlot(slots, slot) : null;
        if (labeled && slot != null && !target) return; // unknown / ambiguous
        const belongs = (c: HabitCompletion) => {
          if (!slots) return true;
          const s = slotOfCompletion(slots, c);
          return !!s && (!target || s.id === target.id);
        };
        if (labeled && !real.some(belongs)) return; // nothing for that slot

        // Applied to the cloud's habit (`decrementChange`): only while it still has that record.
        const effectiveDate = get().testDate || new Date().toISOString().split('T')[0];
        void changeHabit(habitId, decrementChange(date, labeled && target ? target.id : undefined, effectiveDate));
      },

      completeLinked: async (habitId, date, slotRef, opId) => {
        const h0 = get().habits.find((h) => h.id === habitId);
        if (!h0) return { ok: false, created: false };
        const made = () => (get().habits.find((h) => h.id === habitId)?.completions ?? []).some((c) => c.opId === opId);
        // The save carrying THIS tick — or, on a retry (the tick is still unsaved here), the next save, which sends it again.
        let save: Promise<boolean> | undefined;
        if (!made()) {
          const slots = habitSlots(h0, date);
          if (slots) {
            const done = completedSlotsForDate(h0, date);
            const s = slotRef != null ? resolveSlot(slots, slotRef) : slots.find((x) => !done.has(x.id)) ?? null;
            if (!s) return slotRef != null ? { ok: false, created: false } : { ok: true, created: false };
            if (done.has(s.id)) return { ok: true, created: false };
            get().incrementHabit(habitId, date, s.id, { opId, confirmed: true });
          } else {
            if (isHabitCompleteForDate(h0, date)) return { ok: true, created: false };
            get().toggleHabit(habitId, date, { opId, confirmed: true });
          }
          save = latestSave.get(habitId);
        }
        const ok = await (save ?? saveHabit(habitId));
        return { ok, created: made() };
      },

      uncompleteByOp: (habitId, opId) => {
        const h0 = get().habits.find((h) => h.id === habitId);
        if (!h0?.completions?.some((c) => c.opId === opId)) return Promise.resolve(true); // nothing that operation made is left
        const effectiveDate = get().testDate || new Date().toISOString().split('T')[0];
        return changeHabit(habitId, uncompleteOpChange(opId, effectiveDate));
      },

      updateHabit: (habitId, updates) => {
        void changeHabit(habitId, patchChange(updates));
      },

      archiveHabit: (habitId) => {
        // Protected prayer habits can't be archived (managed by Prayer Settings).
        if (get().habits.find((h) => h.id === habitId)?.system) return;
        void changeHabit(habitId, archiveChange(new Date().toISOString()));
      },

      restoreHabit: (habitId) => {
        void changeHabit(habitId, restoreChange);
      },

      deleteHabit: (habitId, opts) => {
        // Protected prayer habits can't be deleted (managed by Prayer Settings) —
        // except via `force` (used by the duplicate-cleanup to remove stray copies).
        if (!opts?.force && get().habits.find((h) => h.id === habitId)?.system) return;
        // ⚠️ BEFORE the habit goes: a care practice linked to it copies the days
        // it was done into its own log, so the practice keeps its history (scope
        // 006). It reads the habit synchronously, so this must precede the set().
        void useCarePracticeStore.getState().onHabitDeleted(habitId);
        loadWindow?.deleted.add(habitId);
        pendingChanges.delete(habitId);
        set((state) => ({
          habits: state.habits.filter((habit) => habit.id !== habitId),
          unsyncedIds: state.unsyncedIds.filter((id) => id !== habitId),
        }));

        void cloudDelete(habitId);
      },

      getActiveHabits: () => {
        // ⭐⭐ "Active" = shown: not archived, and a managed prayer habit only
        // while its Prayer Settings switch is on (owner, 2026-09-17). Home,
        // routines and the habit pickers all read this.
        const { prayer } = useLocationStore.getState();
        return get().habits
          .filter((habit) => !habit.archivedAt && isSystemHabitVisible(habit, prayer))
          .sort((a, b) => (a.order || 0) - (b.order || 0));
      },

      getArchivedHabits: () => {
        return get().habits.filter((habit) => habit.archivedAt);
      },

      reorderHabits: (sourceId, targetId) => {
        const before = new Map(get().habits.map((h) => [h.id, h.order]));
        set((state) => {
          const habits = [...state.habits];
          const sourceIndex = habits.findIndex(h => h.id === sourceId);
          const targetIndex = habits.findIndex(h => h.id === targetId);
          
          if (sourceIndex === -1 || targetIndex === -1) return state;
          
          // Remove source habit
          const [sourceHabit] = habits.splice(sourceIndex, 1);
          
          // Insert at target position
          habits.splice(targetIndex, 0, sourceHabit);
          
          // Update order for all habits
          const updatedHabits = habits.map((habit, index) => ({
            ...habit,
            order: index
          }));
          
          return { habits: updatedHabits };
        });

        // ⚠️⚠️ Only the habits whose position changed, and only their `order`: this used to
        // rewrite EVERY habit whole from this tab's copy — a stale tab's drag erased other
        // devices' ticks on all of them.
        get().habits.forEach((h) => {
          if (before.get(h.id) !== h.order) void changeHabit(h.id, setOrderChange(h.order));
        });
      },
    }),
    {
      name: 'habits-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 6,
      // Persist the cache + who it belongs to; `initializedUserId` stays
      // transient so load-on-login runs each fresh app start.
      partialize: (state) => ({
        habits: state.habits,
        testDate: state.testDate,
        userLevel: state.userLevel,
        cachedUserId: state.cachedUserId,
        // ⚠️ A cache from before this field has none: every habit in it is taken for a
        // cloud copy, so a load drops the ones the cloud no longer has.
        unsyncedIds: state.unsyncedIds,
      }),
      migrate: (persistedState: any, version: number) => {
        if (version < 6) {
          // Reset to empty state if version is too old
          return {
            habits: [],
            testDate: null,
            userLevel: 1
          } as unknown as HabitState;
        }
        return persistedState as HabitState;
      }
    }
  )
);

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: the next account saw the last
   one's habits (and ticked them) until its own load; habits that lived only here (made offline) were
   replaced by the next account's load; and the next account's habits made before its own load stayed
   in the last one's store — its load didn't even run, `initializedUserId` was still its own.
   ⚠️ This tab's unsaved changes (`pendingChanges`) are kept in memory, not in the copy, by habit id:
   they wait out a change of account in this page — the habit's account's next load sends them
   (2026-10-20) — but not a reload, after which that load's cloud copy wins. */
scopeToAccount(useHabitStore);
